import { createContext, useCallback, useContext, useEffect, useState } from 'react';

const DEFAULT_ACCENT = '#3D8E83';
const ACCENT_OPTIONS = ['#3D8E83', '#5B6FB8', '#B86A4A', '#2D2D2A'];

const ThemeContext = createContext(null);

function applyAccent(hex, dark) {
  const root = document.documentElement;
  root.style.setProperty('--accent', hex);
  root.style.setProperty('--accent-strong', `color-mix(in oklch, ${hex} 80%, #000)`);
  root.style.setProperty(
    '--accent-soft',
    dark ? `color-mix(in oklch, ${hex} 22%, #14130F)` : `color-mix(in oklch, ${hex} 14%, #FFFFFF)`,
  );
  root.style.setProperty(
    '--accent-ink',
    dark ? `color-mix(in oklch, ${hex} 60%, #FFFFFF)` : `color-mix(in oklch, ${hex} 70%, #1A1A14)`,
  );
}

export function ThemeProvider({ children }) {
  const [dark, setDarkState] = useState(() => {
    if (typeof window === 'undefined') return false;
    const stored = localStorage.getItem('theme');
    if (stored === 'dark') return true;
    if (stored === 'light') return false;
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  });
  const [accent, setAccentState] = useState(() => {
    return (typeof window !== 'undefined' && localStorage.getItem('accent')) || DEFAULT_ACCENT;
  });

  useEffect(() => {
    document.documentElement.dataset.theme = dark ? 'dark' : 'light';
    applyAccent(accent, dark);
  }, [dark, accent]);

  const setDark = useCallback((v) => {
    setDarkState(v);
    localStorage.setItem('theme', v ? 'dark' : 'light');
  }, []);

  const setAccent = useCallback((v) => {
    setAccentState(v);
    localStorage.setItem('accent', v);
  }, []);

  return (
    <ThemeContext.Provider value={{ dark, setDark, accent, setAccent, accentOptions: ACCENT_OPTIONS }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme outside ThemeProvider');
  return ctx;
}
